/**
=========================================================
* SuitOrbit - Rutas de Nómina
=========================================================

  Conjunto de rutas dedicado al módulo de Nómina.
  Sigue el mismo esquema que `routescont.js` y `routesfac.js`:

  1. `type: "collapse"`  → entrada de menú navegable.
  2. `type: "title"`     → título dentro del Sidenav.
  3. `type: "divider"`   → divisor visual.
  4. `name`              → texto visible en el Sidenav.
  5. `key`               → identificador único (también usado
                            por `tienePermisosModulo`).
  6. `icon`              → icono mostrado en el Sidenav.
  7. `collapse`          → array con sub-rutas (menú anidado).
  8. `route`             → path de react-router para la entrada.
  9. `component`         → componente que se renderiza en la ruta.

  Para añadir una pantalla de nómina, créela en `src/nomina`
  y agregue una entrada al array `routesNom`.
*/

// Layouts
import Dashboard from "layouts/dashboard";
import Entrar from "layouts/login";

// Pantallas de Nómina
import InicioNomina from "nomina/InicioNomina";
import ConfiguracionNomina from "nomina/ConfiguracionNomina";
import CatalogosNomina from "nomina/CatalogosNomina";
import { IniciarNominaPage } from "nomina/IniciarNomina";
import { DefinicionNominaPage } from "nomina/DefinicionNomina";
import { AplicarDescuentosPage } from "nomina/AplicarDescuentos";
import { ExcluirEmpleadoNominaPage } from "nomina/ExcluirEmpleadoNomina";
import { CerrarNominaPage } from "nomina/CerrarNomina";
import RegistroEmpleados from "nomina/RegistroEmpleados";
import NovedadesNomina from "nomina/NovedadesNomina";
import RegistroPrestamos from "nomina/RegistroPrestamos";
import { ListadoIngresos, ListadoOtrosDescuentos } from "nomina/ReportesNomina";

import Icon from "@mui/material/Icon";

const routesNom = [
  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/dashboard",
    component: <Dashboard />,
  },
  {
    type: "collapse",
    name: "Nómina",
    key: "nomina",
    programa: "nomina",
    // Solo umbrella: cada hijo se muestra por su propio `key`.
    // El grupo aparece si hay al menos un hijo con permiso (SidebarNom).
    icon: <Icon fontSize="small">badge</Icon>,
    route: "/nomina/inicio",
    component: "",
    collapse: [
      {
        type: "collapse",
        name: "Inicio",
        key: "inicionomina",
        route: "/nomina/inicio",
        component: <InicioNomina />,
      },
      {
        type: "collapse",
        name: "Registro de Empleados",
        key: "registroempleados",
        exigePrograma: true,
        route: "/nomina/empleados",
        component: <RegistroEmpleados />,
      },
      {
        type: "collapse",
        name: "Novedades",
        key: "novedadesnomina",
        exigePrograma: true,
        route: "/nomina/novedades",
        component: <NovedadesNomina />,
      },
    ],
  },
  {
    type: "collapse",
    name: "Préstamos",
    key: "prestamosnomina",
    programa: "nomina",
    exigePrograma: true,
    icon: <Icon fontSize="small">account_balance_wallet</Icon>,
    route: "/nomina/prestamos",
    component: "",
    collapse: [
      {
        type: "collapse",
        name: "Registro de Préstamos",
        key: "prestamosnomina",
        exigePrograma: true,
        route: "/nomina/prestamos",
        component: <RegistroPrestamos />,
      },
    ],
  },
  {
    type: "collapse",
    name: "Reportes",
    key: "reportesnomina",
    programa: "nomina",
    icon: <Icon fontSize="small">assessment</Icon>,
    route: "/nomina/reportes/ingresos",
    component: "",
    collapse: [
      {
        type: "collapse",
        name: "Listado de Ingresos",
        key: "listadoingresos",
        exigePrograma: true,
        route: "/nomina/reportes/ingresos",
        component: <ListadoIngresos />,
      },
      {
        type: "collapse",
        name: "Listado de Otros Descuentos",
        key: "listadootrosdescuentos",
        exigePrograma: true,
        route: "/nomina/reportes/descuentos",
        component: <ListadoOtrosDescuentos />,
      },
    ],
  },
  {
    type: "collapse",
    name: "Configuración",
    key: "configuracionnomina",
    // Comparte el umbrella «nomina», como Ventas usa «mantenimientoventas»:
    // SidebarNom hereda el programa del grupo (igual que SidebarFac).
    programa: "nomina",
    icon: <Icon fontSize="small">settings</Icon>,
    route: "/nomina/configuracion",
    component: "",
    collapse: [
      {
        type: "collapse",
        name: "Nómina inicial",
        key: "nominainicial",
        route: "/nomina/configuracion",
        component: <ConfiguracionNomina />,
      },
      {
        type: "collapse",
        name: "Configuracion General",
        key: "catalogosnomina",
        route: "/nomina/configuracion/catalogos",
        component: <CatalogosNomina />,
      },
      {
        type: "collapse",
        name: "Iniciar nómina",
        key: "iniciarnomina",
        exigePrograma: true,
        route: "/nomina/configuracion/iniciar",
        component: <IniciarNominaPage />,
      },
      {
        type: "collapse",
        name: "Definición de nómina",
        key: "definicionnomina",
        exigePrograma: true,
        route: "/nomina/configuracion/definir",
        component: <DefinicionNominaPage />,
      },
      {
        type: "collapse",
        name: "Aplicar Descuentos",
        key: "aplicardescuentos",
        exigePrograma: true,
        route: "/nomina/configuracion/descuentos",
        component: <AplicarDescuentosPage />,
      },
      {
        type: "collapse",
        name: "Excluir empleados",
        key: "excluirempleados",
        exigePrograma: true,
        route: "/nomina/configuracion/excluir",
        component: <ExcluirEmpleadoNominaPage />,
      },
      {
        type: "collapse",
        name: "Cerrar nómina",
        key: "cerrarnomina",
        exigePrograma: true,
        route: "/nomina/configuracion/cerrar",
        component: <CerrarNominaPage />,
      },
    ],
  },
  {
    type: "collapse",
    name: "Sign In",
    key: "sign-in",
    icon: <Icon fontSize="small">login</Icon>,
    route: "/login",
    component: <Entrar />,
  },
  {
    name: "Sign Up",
    key: "sign-up",
    route: "/login",
    component: <Entrar />,
  },
];

export default routesNom;
